import { ReactElement, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { HiOutlineCheckCircle } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import Button from '../../../../components/Button';
import { SessionAnalysisType } from '../../../../types/SessionAnalysisType';
import { SurfaceQuality, SurfaceEnum } from '../../../../types/SurfaceQuality';
import OptionSection from './OptionSection';

interface SurfaceQualitySectionProps {
  session: any;
  updateSession: any;
  invalidateSession: () => Promise<void>;
}

/** Surface-quality editor — renders only for tyre-test sessions. */
export default function SurfaceQualitySection({
  session,
  updateSession,
  invalidateSession,
}: SurfaceQualitySectionProps): ReactElement | null {
  const formMethods = useFormContext();
  const [surfaceQualityObj, setSurfaceQualityObj] = useState<SurfaceQuality>(SurfaceQuality.fromFloat(10.25));
  const [surfaceQualityChanged, setSurfaceQualityChanged] = useState(false);
  const [isUpdatingSurfaceQuality, setIsUpdatingSurfaceQuality] = useState(false);

  // Set initial surface quality from session data
  useEffect(() => {
    if (session?.surface_quality !== undefined && session?.surface_quality !== null) {
      setSurfaceQualityObj(SurfaceQuality.fromFloat(session.surface_quality));
      setSurfaceQualityChanged(false);
    } else {
      // If surface_quality is null, set default and mark as changed so form is dirty
      setSurfaceQualityObj(SurfaceQuality.fromFloat(10.25));
      setSurfaceQualityChanged(true);
    }
  }, [session]);

  const currentAnalysisType = Number(formMethods.watch('analysis_type') || session?.analysis_type);
  const showSurfaceQualityOption =
    currentAnalysisType === SessionAnalysisType.TRAVERSE_TYRE_REFERENCE ||
    currentAnalysisType === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON;

  if (!showSurfaceQualityOption) {
    return null;
  }

  const handleSurfaceQualityChange = (surfaceType: SurfaceEnum, quality: number) => {
    const newSurfaceQuality = new SurfaceQuality(surfaceType, quality);
    setSurfaceQualityObj(newSurfaceQuality);
    const currentValue = session?.surface_quality ?? 10.25;
    setSurfaceQualityChanged(newSurfaceQuality.toFloat() !== currentValue);
  };

  const handleUpdateSurfaceQuality = async () => {
    if (!session) {
      toast.error('Session data not available');
      return;
    }

    try {
      setIsUpdatingSurfaceQuality(true);
      await updateSession.mutateAsync({
        surface_quality: surfaceQualityObj.toFloat(),
      });
      toast.success('Surface quality updated');
      setSurfaceQualityChanged(false);
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating surface quality');
    } finally {
      setIsUpdatingSurfaceQuality(false);
    }
  };

  const savedSurface = session?.surface_quality != null
    ? SurfaceQuality.fromFloat(session.surface_quality).toDict()
    : null;
  const surfaceStatus = savedSurface
    ? `${savedSurface.surface_type_description} ${savedSurface.quality}`
    : 'not set';

  return (
    <OptionSection
      title="Surface Quality"
      status={<span className="cr-text-sm cr-text-gray-500">{surfaceStatus}</span>}
    >
      <div className="cr-mb-4 cr-flex cr-flex-col cr-gap-4 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md lg:cr-flex-row">
        {/* Left Side - Controls */}
        <div className="cr-flex-1 cr-space-y-4">
          {/* Surface Type Dropdown */}
          <div>
            <label
              htmlFor="surface-type-select"
              className="cr-mb-2 cr-block cr-text-sm cr-font-medium cr-text-gray-700"
            >
              Surface Type
            </label>
            <select
              id="surface-type-select"
              className="cr-h-11 cr-w-full cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-4 cr-text-base focus:cr-border-blue-500 focus:cr-outline-none"
              value={surfaceQualityObj.surface_type}
              onChange={(e) =>
                handleSurfaceQualityChange(
                  Number(e.target.value) as SurfaceEnum,
                  surfaceQualityObj.quality,
                )
              }
            >
              <option value={SurfaceEnum.VELODROME}>Velodrome</option>
              <option value={SurfaceEnum.PAVEMENT}>Pavement</option>
              <option value={SurfaceEnum.COBBLES}>Cobbles</option>
              <option value={SurfaceEnum.GRAVEL}>Gravel</option>
              <option value={SurfaceEnum.DIRT}>Dirt</option>
            </select>
          </div>

          {/* Quality Input */}
          <div>
            <label
              htmlFor="quality-input"
              className="cr-mb-2 cr-block cr-text-sm cr-font-medium cr-text-gray-700"
            >
              Quality (0.0 - 1.0)
            </label>
            <div className="cr-flex cr-items-center cr-gap-3">
              <input
                id="quality-input"
                type="number"
                step="0.01"
                min="0"
                max="1"
                className="cr-h-11 cr-w-32 cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-4 cr-text-base focus:cr-border-blue-500 focus:cr-outline-none"
                value={surfaceQualityObj.quality}
                onChange={(e) => {
                  const newQuality = parseFloat(e.target.value);
                  if (!isNaN(newQuality) && newQuality >= 0 && newQuality <= 1) {
                    handleSurfaceQualityChange(surfaceQualityObj.surface_type, newQuality);
                  }
                }}
              />
              <span className="cr-text-sm cr-text-gray-600">0.0 = very smooth, 0.5+ = very rough</span>
            </div>
          </div>

          {/* Current Description */}
          <div className="cr-rounded cr-bg-neutral-100 cr-p-3">
            <p className="cr-text-sm cr-font-medium cr-text-blue-900">
              {surfaceQualityObj.getDescription()}
            </p>
          </div>

          {/* Save Button */}
          {surfaceQualityChanged && (
            <Button
              onClick={handleUpdateSurfaceQuality}
              disabled={isUpdatingSurfaceQuality}
              icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5 cr-stroke-[2.2px]" />}
              normalCase
              className="cr-h-11 cr-font-semibold"
            >
              {isUpdatingSurfaceQuality ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>

        {/* Right Side - Guide */}
        <div className="lg:cr-w-96 lg:cr-border-l lg:cr-border-gray-200 lg:cr-pl-4">
          <h5 className="cr-mb-3 cr-text-sm cr-font-semibold cr-text-gray-800">Surface Quality Guide</h5>
          <div className="cr-space-y-2 cr-text-xs cr-text-gray-600">
            <div className="cr-rounded cr-bg-gray-50 cr-p-2">
              <p className="cr-mb-1 cr-font-semibold">Pavement - Bicycle Roughness Index (BRI)</p>
              <div className="cr-space-y-1 cr-pl-2">
                <p>
                  <span className="cr-font-semibold">Very Smooth (0.0)</span> - Recently paved, smooth
                  blacktop, or high-quality concrete bike paths. Minimal to no perceptible vibration
                  through the handlebars.
                </p>
                <p>
                  <span className="cr-font-semibold">Smooth (0.2)</span> - Standard asphalt in good
                  condition. You can feel a slight texture, but not uncomfortable.
                </p>
                <p>
                  <span className="cr-font-semibold">Fair (0.3)</span> - Older asphalt with some cracking
                  and surface wear. A noticeable and constant &ldquo;buzz&rdquo; or vibration is felt.
                </p>
                <p>
                  <span className="cr-font-semibold">Rough (0.4)</span> - Coarse chip seal, or pavement
                  with frequent cracks and minor potholes. The ride can be uncomfortable over longer
                  distances.
                </p>
                <p>
                  <span className="cr-font-semibold">Very Rough (0.5+)</span> - Significantly deteriorated
                  pavement with potholes and large cracks that require the rider to pay close attention to
                  their line choice. Uncomfortable for most riding.
                </p>
              </div>
            </div>
            <div className="cr-rounded cr-bg-gray-50 cr-p-2">
              <p className="cr-text-xs cr-italic">
                For non-pavement surfaces, quality is subjective. Higher resolution values (e.g., 0.24)
                can be used to differentiate small differences.
              </p>
            </div>
          </div>
        </div>
      </div>
    </OptionSection>
  );
}
